import React, { useRef } from 'react'
import { cn } from '../../lib/cn'

// Variante "underline" (por defecto): subrayado + texto coloreado.
// `active` = pestaña activa (color pleno + fondo tintado suave + subrayado).
// `idle`   = color atenuado persistente cuando la pestaña declara su propio
//            acento (así su identidad de color se ve aun estando inactiva).
const TAB_ACCENTS = {
  social:   { active: 'border-social-700 text-social-800 bg-social-50/70',       idle: 'text-social-700/70 hover:text-social-800 hover:border-social-200' },
  hospital: { active: 'border-hospital-600 text-hospital-700 bg-hospital-50/70', idle: 'text-hospital-600/70 hover:text-hospital-700 hover:border-hospital-200' },
  amber:    { active: 'border-amber-500 text-amber-700 bg-amber-50/80',          idle: 'text-amber-600/75 hover:text-amber-700 hover:border-amber-200' },
  emerald:  { active: 'border-emerald-600 text-emerald-700 bg-emerald-50/70',    idle: 'text-emerald-600/70 hover:text-emerald-700 hover:border-emerald-200' },
  sky:      { active: 'border-sky-600 text-sky-700 bg-sky-50/70',                idle: 'text-sky-600/70 hover:text-sky-700 hover:border-sky-200' },
  violet:   { active: 'border-violet-600 text-violet-700 bg-violet-50/70',       idle: 'text-violet-600/70 hover:text-violet-700 hover:border-violet-200' },
  rose:     { active: 'border-rose-600 text-rose-700 bg-rose-50/70',             idle: 'text-rose-600/70 hover:text-rose-700 hover:border-rose-200' },
}

// Variante "solid" (pill): la pestaña activa es un bloque de color SÓLIDO con
// texto blanco; las inactivas muestran una versión clara/tintada de su color.
const TAB_SOLID = {
  social:   { active: 'bg-social-700 text-white border-social-700 shadow-sm shadow-social-200',       idle: 'bg-social-50 text-social-700 border-social-200 hover:bg-social-100' },
  hospital: { active: 'bg-hospital-600 text-white border-hospital-600 shadow-sm shadow-hospital-200', idle: 'bg-hospital-50 text-hospital-700 border-hospital-200 hover:bg-hospital-100' },
  amber:    { active: 'bg-amber-500 text-white border-amber-500 shadow-sm shadow-amber-200',          idle: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' },
  emerald:  { active: 'bg-emerald-600 text-white border-emerald-600 shadow-sm shadow-emerald-200',    idle: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' },
  sky:      { active: 'bg-sky-600 text-white border-sky-600 shadow-sm shadow-sky-200',                idle: 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100' },
  violet:   { active: 'bg-violet-600 text-white border-violet-600 shadow-sm shadow-violet-200',       idle: 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100' },
  rose:     { active: 'bg-rose-600 text-white border-rose-600 shadow-sm shadow-rose-200',             idle: 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100' },
}

/**
 * Tabs accesibles del Design System.
 * @param {object} props
 * @param {{ key: string, label: React.ReactNode, icon?: React.ReactNode, badge?: React.ReactNode, disabled?: boolean, accent?: keyof typeof TAB_ACCENTS }[]} props.tabs
 * @param {string} props.value - key de la pestaña activa
 * @param {(key: string) => void} props.onChange
 * @param {keyof typeof TAB_ACCENTS} [props.accent] - acento por defecto; cada tab puede sobreescribirlo con su propio `accent`
 * @param {boolean} [props.fill] - distribuye las pestañas a ancho completo
 * @param {boolean} [props.solid] - variante "pill" con color sólido en la pestaña activa
 */
export function Tabs({ tabs = [], value, onChange, accent = 'social', fill = false, solid = false, segmented = false, buttons = false, className, ...rest }) {
  const refs = useRef([])

  const handleKeyDown = (e, idx) => {
    const enabled = tabs.map((t, i) => (t.disabled ? null : i)).filter((i) => i !== null)
    const pos = enabled.indexOf(idx)
    let next = null
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = enabled[(pos + 1) % enabled.length]
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = enabled[(pos - 1 + enabled.length) % enabled.length]
    else if (e.key === 'Home') next = enabled[0]
    else if (e.key === 'End') next = enabled[enabled.length - 1]
    if (next !== null && next !== undefined) {
      e.preventDefault()
      onChange?.(tabs[next].key)
      refs.current[next]?.focus()
    }
  }

  return (
    <div
      role="tablist"
      aria-orientation="horizontal"
      className={cn(
        'flex overflow-x-auto scrollbar-hide',
        buttons
          ? 'w-full gap-3 p-1'
          : segmented
            ? 'w-fit max-w-full gap-1 rounded-2xl bg-slate-100/90 p-1'
            : solid ? 'gap-2 p-0.5' : 'gap-1 border-b border-hairline',
        className
      )}
      {...rest}
    >
      {tabs.map((tab, idx) => {
        const active = tab.key === value
        const map = solid ? TAB_SOLID : TAB_ACCENTS
        const acc = map[tab.accent] || map[accent] || map.social

        let stateClass
        if (buttons) {
          // Botones grandes: la activa es un bloque verde pleno; las demás, botones
          // blancos con borde y sombra, para que se lea que se pueden tocar.
          stateClass = active
            ? 'border border-social-700 bg-social-700 text-white shadow-lg shadow-social-700/25'
            : 'border border-hairline bg-white text-ink shadow-sm hover:-translate-y-px hover:border-social-300 hover:shadow-md'
        } else if (segmented) {
          // Control segmentado: la activa "flota" en blanco sobre el carril gris.
          stateClass = active
            ? 'bg-white text-social-800 shadow-sm ring-1 ring-black/5'
            : 'text-ink-soft hover:text-ink hover:bg-white/60'
        } else if (solid) {
          stateClass = active ? acc.active : acc.idle
        } else {
          // underline: las pestañas con acento propio conservan color aun inactivas;
          // las demás usan el gris neutro clásico.
          stateClass = active
            ? acc.active
            : tab.accent
              ? cn('border-transparent', acc.idle, 'hover:bg-slate-50/40')
              : 'border-transparent text-ink-soft hover:text-ink hover:border-slate-300'
        }

        return (
          <button
            key={tab.key}
            ref={(el) => (refs.current[idx] = el)}
            role="tab"
            type="button"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            disabled={tab.disabled}
            onClick={() => onChange?.(tab.key)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            className={cn(
              'group inline-flex items-center gap-2 whitespace-nowrap px-4 py-2.5 text-sm font-semibold transition-all',
              buttons && 'min-h-[3.25rem] flex-1 justify-center rounded-2xl px-5 py-3 text-base font-bold',
              buttons
                ? ''
                : segmented
                ? 'rounded-xl'
                : solid
                  ? 'rounded-xl border'
                  : 'border-b-2 -mb-px rounded-t-lg',
              fill && 'flex-1 justify-center',
              stateClass,
              tab.disabled && 'opacity-40 cursor-not-allowed'
            )}
          >
            {tab.icon && <span className={cn('flex-shrink-0', buttons ? 'h-5 w-5' : 'h-4 w-4')}>{tab.icon}</span>}
            <span>{tab.label}</span>
            {tab.badge != null && (
              <span
                className={cn(
                  'ml-0.5 inline-flex items-center justify-center rounded-full px-1.5 min-w-[1.25rem] h-5 text-[11px] font-bold',
                  buttons && 'h-6 min-w-[1.6rem] text-xs',
                  active
                    ? buttons
                      ? 'bg-white text-social-800'
                      : solid && !segmented
                        ? 'bg-white/25 text-white'
                        : 'bg-social-700 text-white'
                    : buttons
                      ? 'bg-social-100 text-social-800'
                      : segmented
                      ? 'bg-white text-ink-soft'
                      : 'bg-slate-100 text-ink-soft group-hover:bg-slate-200'
                )}
              >
                {tab.badge}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

export default Tabs
